<template>
  <div class="page">
    <!--Header-->
    <div class="page-header flex-shrink-0 bg-white border-bottom">
      <div class="container-fluid px-md-4 px-3">
        <div class="row no-gutters d-flex justify-content-between align-items-center">
          <h3 class="mb-0">
            <i class="fas fa-layer-group"></i>&nbsp;Policy Containers
            <b-spinner class="ml-2" v-show="isBusy" small label="Loading"></b-spinner>
          </h3>
          <b-form inline>
            <router-link to="/addeditpolicycontainer" class="btn btn-success">
              <i class="fas fa-plus"></i>
              <span class="d-md-inline d-none"> Add Container</span>
            </router-link>
          </b-form>
        </div>
      </div>
    </div>

    <!--Filters-->
    <div class="container-fluid px-md-4 px-3 pt-2 pb-3 bg-white border-bottom mb-3">
      <div class="row no-gutters align-items-end">
        <div class="col-auto">
          <b-form-checkbox v-model="includeArchived" @change="refresh">
            Show archived
          </b-form-checkbox>
        </div>
      </div>
    </div>

    <!--Table-->
    <div v-show="containers.length" class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
      <b-table
        id="containers-table"
        bordered
        class="mb-5 rg-table"
        :items="containerProvider"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :busy.sync="isBusy"
      >
        <template v-slot:cell(name)="data">
          {{ data.item.name }}
          <div v-if="data.item.layers && data.item.layers.length" class="mt-1">
            <div
              v-for="(layer, idx) in data.item.layers"
              :key="layer.id || idx"
              class="text-muted small"
            >
              {{ layer.name || `(Layer ${idx + 1})` }}
              &mdash;
              ${{ (layer.attachmentPoint || 0).toLocaleString('en-US') }}
              xs
              ${{ (layer.layerLimit || 0).toLocaleString('en-US') }}
              <template v-if="layer.participations && layer.participations.length">
                &mdash;
                {{ layer.participations.map(p => p.carrierName).filter(Boolean).join(', ') }}
              </template>
            </div>
          </div>
        </template>

        <template v-slot:cell(startDate)="data">
          {{ data.item.startDate | formatDateNoTime }}
        </template>

        <template v-slot:cell(endDate)="data">
          {{ data.item.endDate | formatDateNoTime }}
        </template>

        <template v-slot:cell(policyCount)="data">
          <b-badge variant="secondary">
            {{ (data.item.policyIds || []).length }}
          </b-badge>
        </template>

        <template v-slot:cell(claimCount)="data">
          <b-badge :variant="data.item.claimCount > 0 ? 'info' : 'light'">
            {{ data.item.claimCount || 0 }}
          </b-badge>
        </template>

        <template v-slot:cell(isArchived)="data">
          <b-badge v-if="data.item.isArchived" variant="secondary">Archived</b-badge>
          <b-badge v-else variant="success">Active</b-badge>
        </template>

        <template v-slot:cell(id)="data">
          <div class="d-flex">
            <router-link
              title="Edit Container"
              :to="{ name: 'AddEditPolicyContainer', params: { id: data.item.id } }"
              class="btn btn-link"
            ><i class="fas fa-edit"></i></router-link>
            <div class="btn btn-link">
              <i class="fas fa-trash text-danger" @click="doDelete(data.item.id, data.item.name)"></i>
            </div>
          </div>
        </template>
      </b-table>
    </div>

    <div
      class="no-results d-flex justify-content-center align-items-center p-4 text-black-50 mx-4"
      v-if="!isBusy && containers.length === 0"
    >
      No policy containers found
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'PolicyContainers',
  data () {
    return {
      isBusy: false,
      sortBy: 'name',
      sortDesc: false,
      includeArchived: false,
      containers: [],
      fields: [
        { key: 'name',        label: 'Name',       sortable: true },
        { key: 'startDate',   label: 'Start Date', sortable: true },
        { key: 'endDate',     label: 'End Date',   sortable: true },
        { key: 'policyCount', label: 'Policies',   sortable: false },
        { key: 'claimCount',  label: 'Claims',     sortable: true },
        { key: 'isArchived',  label: 'Status',     sortable: true },
        { key: 'id',          label: 'Actions' }
      ]
    }
  },
  methods: {
    ...mapActions('company', ['getPolicyContainers', 'deletePolicyContainer']),

    async containerProvider (ctx) {
      this.isBusy = true
      try {
        const result = await this.getPolicyContainers({ includeArchived: this.includeArchived })
        let items = (result && result.data && result.data.containers) ? result.data.containers : []

        if (ctx.sortBy) {
          items.sort((a, b) => {
            let aVal = a[ctx.sortBy] || ''
            let bVal = b[ctx.sortBy] || ''
            if (typeof aVal === 'string') aVal = aVal.toLowerCase()
            if (typeof bVal === 'string') bVal = bVal.toLowerCase()
            if (aVal < bVal) return ctx.sortDesc ? 1 : -1
            if (aVal > bVal) return ctx.sortDesc ? -1 : 1
            return 0
          })
        }

        this.containers = items
        return items
      } catch (e) {
        console.error('Error loading policy containers:', e)
        this.containers = []
        return []
      } finally {
        this.isBusy = false
      }
    },

    refresh () {
      this.$root.$emit('bv::refresh::table', 'containers-table')
    },

    async doDelete (containerId, name) {
      const confirmed = confirm(`Delete container "${name}"? This cannot be undone.`)
      if (!confirmed) return
      await this.deletePolicyContainer(containerId)
      this.$toasted.success('Policy Container deleted.', { duration: 4000 })
      this.refresh()
    }
  }
}
</script>

<style scoped>
.no-results {
  border: 1px dashed #ccc;
  border-radius: 10px;
  background: #f9f9f9;
}
</style>
